// Subpath '@werkbank/calc/format' (Optimierung 2026-08-25): haelt decimal.js
// aus dem Haupt-Chunk — die Formatierer sind dezimalfrei.
import { formatAmountDe, formatDateDe, formatDateTimeDe, parseDateDe } from '@werkbank/calc/format';
import { minutesToHM } from '@werkbank/timekeeping';

/**
 * Cent-Betrag als deutsche Zahl OHNE Währungssymbol; das „€" hängen die
 * Aufrufer selbst an. Nutzt das kanonische @werkbank/calc-Primitiv, damit
 * Web und Server identisch gruppieren.
 */
export function formatCents(cents: number): string {
  return formatAmountDe(cents);
}

/**
 * Vollanalyse S2: kanonischer Minuten→„H:MM"-Formatter der Web-UI (vorher
 * fünffach lokal kopiert). Implementierung = ehemals `clockDisplay.hmDuration`:
 * runden, nie negativ, keine führende Stunden-Null — die Ziffernfolge kommt
 * aus dem kanonischen `minutesToHM` des Pakets. Ein „ h"-Suffix hängen die
 * Aufrufer selbst an.
 */
export function formatHm(min: number): string {
  return minutesToHM(Math.max(0, Math.round(min)));
}

/**
 * Vollanalyse S2: kanonischer SIGNIERTER Minuten→„±H:MM"-Formatter (Salden,
 * Zeitkonto). Semantik aus users/timeFormat.formatSignedHm: auf ganze Minuten
 * gerundet (nie „…:0.4") und Minuszeichen (U+2212) NUR bei abs > 0 — also nie
 * „−0:00". Die frühere AttendancePage-Kopie (ohne Rundung/Guard) ist damit
 * bewusst auf diese Semantik angeglichen.
 */
export function formatSignedHm(min: number): string {
  const abs = Math.round(Math.abs(min));
  const sign = min < 0 && abs > 0 ? '−' : '';
  return `${sign}${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, '0')}`;
}

/**
 * Vollanalyse S2: ISO-indizierte Wochentags-Kürzel (1 = Mo … 7 = So; Index 0
 * leer) — einzige Quelle der Web-UI, vorher lokal in der AttendancePage.
 */
export const WEEKDAYS = ['', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'] as const;

/**
 * Einzige Datumsanzeige der UI (D-M0-K1 D3): ISO (Datum oder Zeitstempel)
 * nach TT.MM.JJJJ. ISO bleibt reines API-Format. Die Regex toleriert
 * Zeitstempel/leere/Nicht-ISO-Werte (Web-Vertrag); die eigentliche
 * TT.MM.JJJJ-Zusammensetzung liegt in @werkbank/calc.
 */
export function formatDateDE(iso: string | null | undefined): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return formatDateDe(iso.slice(0, 10));
}

/**
 * Kundentest 2026-08 (Plantafel): Kalenderwoche nach ISO 8601 für ein
 * ISO-Datum („JJJJ-MM-TT"). Der Donnerstag der Woche bestimmt Jahr und KW —
 * damit stimmen die Jahreswechsel-Kanten (29.12.–3.1.). Rechnet auf UTC-
 * Mitternacht (keine DST-Kanten); ungültige Eingabe ergibt 0.
 */
export function isoWeekNumber(iso: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
  if (!m) return 0;
  const d = new Date(`${m[1]}-${m[2]}-${m[3]}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return 0;
  // Auf den Donnerstag DIESER Woche schieben (Mo=0 … So=6).
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7) + 3);
  // Erster Donnerstag des (ggf. verschobenen) ISO-Jahres: Woche des 4. Januar.
  const firstThursday = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  firstThursday.setUTCDate(firstThursday.getUTCDate() - ((firstThursday.getUTCDay() + 6) % 7) + 3);
  return 1 + Math.round((d.getTime() - firstThursday.getTime()) / (7 * 86_400_000));
}

/**
 * Vollanalyse M12: Europe/Berlin ist die VERBINDLICHE Anzeige-Zeitzone der
 * Web-UI für Zeitstempel — unabhängig von der Zeitzone des Browsers zeigen
 * damit alle Seiten (Anwesenheit, Aktivität, Plantafel, Uhr, Geräte, Versand)
 * dieselbe Uhrzeit für denselben Stempel.
 */

/** ISO-Zeitstempel → „TT.MM.JJJJ, HH:MM" in Berliner Zeit; '' bei leer/ungültig. */
export function formatDateTimeDE(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // Kanon aus @werkbank/calc (Optimierung 2026-08-25, Befund H2): Web und
  // Beleg-PDF setzen den Zeitstempel damit garantiert identisch zusammen
  // (formatToParts, Europe/Berlin) — vorher zwei Intl-Implementierungen.
  return formatDateTimeDe(iso);
}

/** ISO-Zeitstempel → „HH:MM" in Berliner Zeit; '' bei leer/ungültig. */
export function formatTimeDE(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Berlin',
  });
}

/**
 * Gegenstück für Eingabefelder: TT.MM.JJJJ nach ISO. Leer ergibt '',
 * unvollständige/ungültige Eingabe ergibt null (weitertippen lassen).
 */
export const parseDateDE = parseDateDe;

/**
 * N7: ISO-Datum (`YYYY-MM-DD`) → lokales `Date` für die @mantine/dates-Picker.
 * Bewusst lokale Mitternacht (`T00:00:00`), damit der ausgewählte Tag ohne
 * UTC-Verschiebung wieder derselbe ISO-Tag wird. Leerer/ungültiger Wert → null.
 */
export function isoToDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const d = new Date(`${m[1]}-${m[2]}-${m[3]}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * N7: `Date` → ISO-Datum (`YYYY-MM-DD`) für die API. Lokale Feldwerte (kein
 * `toISOString`, das würde in UTC verschieben). null → ''.
 */
export function dateToIso(d: Date | null): string {
  if (!d || Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${da}`;
}

/**
 * Zentraler Zähl-Formatter (D-W1 QS-1): „1 Entwurf", „2 Entwürfe" — nie mehr
 * „1 Entwürfe". Singular ist Pflicht, damit kein Aufrufer ihn vergisst.
 */
export function formatCount(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/**
 * Y-Achsen-Ticks für Euro-Charts (D-W1 DA-1/AW-1): echte Beträge mit
 * deutscher Gruppierung statt gerundeter „1k"-Ticks, die bei Betriebsgrößen
 * von 3–40 Tsd. € mehrfach denselben Text ergeben.
 */
export function formatEuroAxisTick(value: number): string {
  return new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(value);
}

/**
 * Vollanalyse S1: Enum-Labels (Belegart, Mahnstufe, Steuersatz, Positionsart)
 * leben kanonisch in @werkbank/shared und werden hier für die Seiten
 * re-exportiert — bestehende '../lib/format'-Importpfade bleiben stabil.
 *
 * DOC_TYPE_LABELS deckt ALLE DocType-Enum-Werte ab (schema.prisma). Fehlt
 * ein Wert, zeigt das Journal eine leere Belegart-Zelle (P1-E §4 Bug bei
 * WORK_REPORT) — der Belegarten-Matrix-Test in format.test.ts sichert das.
 */
export {
  DOC_TYPE_LABELS,
  DUNNING_LEVEL_LABELS,
  INVOICE_KIND_LABELS,
  ITEM_KIND_LABELS,
  TAX_CODE_LABELS,
} from '@werkbank/shared';

/**
 * Kurzzusatz hinter „Rechnung" in Listen und Titeln: „ (Abschlag)",
 * „ (Teilrechnung)", „ (Schluss)". Kundentest 2026-08: vorher stand die
 * Ternär-Kette an vier Stellen kopiert — eine neue Rechnungsart fiel dort
 * stillschweigend durch und erschien als schlichte „Rechnung".
 */
const KIND_SUFFIX: Record<string, string> = {
  PARTIAL: ' (Abschlag)',
  INTERIM: ' (Teilrechnung)',
  FINAL: ' (Schluss)',
};
export function invoiceKindSuffix(kind: string | null | undefined): string {
  return kind ? (KIND_SUFFIX[kind] ?? '') : '';
}

/**
 * Vollanalyse S1: EINZIGE Quelle der deutschen Status-Labels der Web-UI
 * (StatusBadge, Filter-Selects, Kopf-Meta). Status-Labels sind web-only,
 * deshalb leben sie hier statt in @werkbank/shared. Die Schlüssel decken
 * Beleg- (DocStatus), Auftrags- (OrderStatus), OP-/Kennzeichen- und
 * Versandprotokoll-Status ab; Farbe/Icon je Status hält StatusBadge.
 */
export const STATUS_LABELS: Record<string, string> = {
  // Belege (DocStatus)
  DRAFT: 'Entwurf',
  FINALIZED: 'Festgeschrieben',
  SENT: 'Versendet',
  PAID: 'Bezahlt',
  CANCELLED: 'Storniert',
  // Aufträge (OrderStatus; DRAFT/CANCELLED teilen sich den Text mit oben)
  ACTIVE: 'Aktiv',
  // Faruq 2026-08-25: fehlte hier — die Pille fiel deshalb auf das Rohwort
  // „IN_PROGRESS" in Entwurfs-Grau zurück. Der Status entsteht mit dem ersten
  // Arbeitsstempel und wird nie von Hand gesetzt.
  IN_PROGRESS: 'In Bearbeitung',
  DONE: 'Fertig',
  BILLED: 'Abgerechnet',
  CLOSED: 'Geschlossen',
  // OP-Semantik und Kennzeichen
  DUE: 'Fällig',
  OVERDUE: 'Überfällig',
  REVIEW: 'Prüfen',
  HOLD: 'On-hold',
  ADHOC: 'Ad-hoc',
  EMERGENCY: 'Notdienst',
  // Versandprotokoll (EmailLog)
  QUEUED: 'Wartet',
  FAILED: 'Fehlgeschlagen',
  // Angebots-Ausgang der Wiedervorlage (D-W1 QS-5)
  QUOTE_OPEN: 'Offen',
  WON: 'Gewonnen',
  LOST: 'Verloren',
};

/** S1: Teilmengen als pick der jeweiligen Keys statt eigener Literale. */
function pickStatusLabels(keys: readonly string[]): Record<string, string> {
  return Object.fromEntries(keys.map((k) => [k, STATUS_LABELS[k] ?? k]));
}

/** Beleg-Status (DocStatus) — Filter-Select und Kopf-Meta im Belegjournal. */
export const DOC_STATUS_LABELS: Record<string, string> = pickStatusLabels([
  'DRAFT',
  'FINALIZED',
  'SENT',
  'PAID',
  'CANCELLED',
]);

/** Auftrags-Status (OrderStatus) — Filter-Select und Kopf-Meta der Auftragsliste. */
export const ORDER_STATUS_LABELS: Record<string, string> = pickStatusLabels([
  'DRAFT',
  'ACTIVE',
  'IN_PROGRESS',
  'DONE',
  'BILLED',
  'CLOSED',
  'CANCELLED',
]);

/**
 * Rechnungskanal-Labels (F15, Optimierung 2026-08-25): lagen in der SEITE
 * CustomersPage, wurden aber von drei weiteren Modulen rueckwaerts importiert
 * — Label-Maps gehoeren neben STATUS_LABELS in die lib.
 */
export const CHANNEL_LABELS: Record<string, string> = {
  XRECHNUNG_EMAIL: 'XRechnung (E-Mail)',
  ZUGFERD_EMAIL: 'ZUGFeRD (E-Mail)',
  PDF_EMAIL: 'PDF (E-Mail)',
  PORTAL_MANUAL: 'Portal (manuell)',
};

/**
 * F14 (Optimierung 2026-08-25): Label-Map → Select-Options — der
 * Object.entries-Einzeiler stand zehnmal im Web (mit Querverweis-Kommentaren
 * aufeinander); Muster wie COMMISSION_TYPE_OPTIONS.
 */
export function selectOptions(map: Record<string, string>): { value: string; label: string }[] {
  return Object.entries(map).map(([value, label]) => ({ value, label }));
}

/**
 * Plantafel-Live-Pillen (Cross 1.2). Entscheidung Faruq 2026-08-26: derselbe
 * Zustand hieß im Büro „Geplant" und in der App „Offen" — jetzt überall
 * „Offen".
 */
export const LIVE_STATUS_LABELS: Record<string, string> = {
  paused: 'Pausiert',
  done: 'Fertig',
  planned: 'Offen',
};
